import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  HostListener,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  Building2,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock,
  Edit,
  Eye,
  EyeOff,
  Lock,
  LucideAngularModule,
  MoreVertical,
  Phone,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-angular';
import { MultiSelectModule } from 'primeng/multiselect';
import { DatePickerModule } from 'primeng/datepicker';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';

import {
  CUSTOMER_PERMISSIONS,
  PERMISSION_GROUPS,
  PermissionGroup,
  PermissionKey,
  ROLE_TEMPLATES,
  RoleTemplate,
} from '../../core/config/customer-permissions.config';
import { EmployeeDto, WorkScheduleDto } from '../../core/models/employee.models';
import { Store } from '../../core/models/store.models';
import { EmployeeService } from '../../core/services/employee.service';
import { LanguageService } from '../../core/services/language.service';
import { StoreService } from '../../core/services/store.service';

interface EmployeeFormModel {
  fullName: string;
  phoneNumber: string;
  password: string;
  isActive: boolean;
}

@Component({
  selector: 'app-employees',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    TranslatePipe,
    MultiSelectModule,
    DatePickerModule,
    ConfirmDialogModule,
  ],
  providers: [ConfirmationService],
  templateUrl: './employees.component.html',
})
export class EmployeesComponent implements OnInit {
  private readonly employeeService = inject(EmployeeService);
  private readonly storeService = inject(StoreService);
  private readonly translateService = inject(TranslateService);
  private readonly languageService = inject(LanguageService);
  private readonly confirmSvc = inject(ConfirmationService);
  private readonly destroyRef = inject(DestroyRef);

  Plus = Plus;
  Search = Search;
  MoreVertical = MoreVertical;
  Edit = Edit;
  Trash2 = Trash2;
  Phone = Phone;
  Building2 = Building2;
  CalendarDays = CalendarDays;
  Clock = Clock;
  ChevronLeft = ChevronLeft;
  ChevronRight = ChevronRight;
  X = X;
  Lock = Lock;
  Eye = Eye;
  EyeOff = EyeOff;

  readonly pageSize = 3;
  readonly permissionGroups = PERMISSION_GROUPS;
  readonly roleTemplates = ROLE_TEMPLATES;
  readonly currentLang = this.languageService.currentLang;

  employeeList = signal<EmployeeDto[]>([]);
  totalCount = signal(0);
  loading = signal(false);
  activeMenuId = signal<string | null>(null);
  stores = signal<Store[]>([]);

  searchQuery = '';

  currentPage = signal(1);

  formVisible = false;
  editingEmployee: EmployeeDto | null = null;
  saving = false;
  showPassword = false;
  empPhoneCode = '+998';
  empForm: EmployeeFormModel = this.createEmptyForm();

  assignDialogVisible = false;
  assigningEmployee: EmployeeDto | null = null;
  selectedStoreIds: string[] = [];
  assigning = false;

  permDialogVisible = false;
  permEmployee: EmployeeDto | null = null;
  selectedPermissions: PermissionKey[] = [];
  savingPerms = false;

  createdPin: string | null = null;
  createdName = '';

  // Work schedule dialog
  scheduleDialogVisible = false;
  scheduleEmployee: EmployeeDto | null = null;
  scheduleLoading = signal(false);
  scheduleSaving = signal(false);
  scheduleForm: { workDays: number; checkInTime: Date; checkOutTime: Date } =
    this.defaultScheduleForm();

  readonly weekDays = [
    { label: 'Du', value: 2 }, // Mon = 1<<1
    { label: 'Se', value: 4 }, // Tue = 1<<2
    { label: 'Cho', value: 8 }, // Wed = 1<<3
    { label: 'Pay', value: 16 }, // Thu = 1<<4
    { label: 'Ju', value: 32 }, // Fri = 1<<5
    { label: 'Sha', value: 64 }, // Sat = 1<<6
    { label: 'Yak', value: 1 }, // Sun = 1<<0
  ];

  readonly permissionLabelMap = computed(() => {
    this.currentLang();
    return new Map(
      CUSTOMER_PERMISSIONS.map((permission) => [
        permission.key,
        this.translateService.instant(permission.labelKey),
      ]),
    );
  });

  readonly stats = computed(() => {
    const list = this.employeeList();

    return {
      total: this.totalCount(),
      active: list.filter((employee) => employee.isActive).length,
      managers: list.filter((employee) => this.isStoreManager(employee)).length,
      associates: list.filter((employee) => this.isSalesAssociate(employee)).length,
    };
  });

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalCount() / this.pageSize));
  }

  get pageEnd(): number {
    return Math.min(this.currentPage() * this.pageSize, this.totalCount());
  }

  get pageRange(): number[] {
    return Array.from({ length: this.totalPages }, (_, index) => index + 1);
  }

  get isEditMode(): boolean {
    return !!this.editingEmployee;
  }

  @HostListener('document:click')
  closeMenus() {
    this.activeMenuId.set(null);
  }

  ngOnInit() {
    this.storeService
      .getStores()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => this.stores.set(response.data?.items ?? []),
      });

    this.load();
  }

  load() {
    this.loading.set(true);

    this.employeeService
      .getAll(this.currentPage(), this.pageSize, this.searchQuery || undefined)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.employeeList.set(response.data?.items ?? []);
          this.totalCount.set(response.data?.totalCount ?? 0);
        },
        error: () => this.loading.set(false),
        complete: () => this.loading.set(false),
      });
  }

  onSearch() {
    this.currentPage.set(1);
    this.load();
  }

  goPage(page: number) {
    if (page < 1 || page > this.totalPages) {
      return;
    }

    this.currentPage.set(page);
    this.load();
  }

  toggleMenu(employeeId: string, event: Event) {
    event.stopPropagation();
    this.activeMenuId.set(this.activeMenuId() === employeeId ? null : employeeId);
  }

  openCreate() {
    this.editingEmployee = null;
    this.empForm = this.createEmptyForm();
    this.showPassword = false;
    this.empPhoneCode = '+998';
    this.formVisible = true;
  }

  openEdit(employee: EmployeeDto) {
    this.editingEmployee = employee;
    this.empForm = {
      fullName: employee.fullName,
      phoneNumber: employee.phoneNumber,
      password: '',
      isActive: employee.isActive,
    };
    this.showPassword = false;
    this.formVisible = true;
  }

  closeForm() {
    this.formVisible = false;
    this.editingEmployee = null;
    this.empForm = this.createEmptyForm();
  }

  submitForm() {
    if (this.isEditMode) {
      this.saveEdit();
      return;
    }

    this.saveCreate();
  }

  deleteEmployee(employee: EmployeeDto) {
    this.confirmSvc.confirm({
      header: this.translateService.instant('EMPLOYEES.DELETE_TITLE'),
      message: this.translateService.instant('EMPLOYEES.DELETE_CONFIRM', {
        name: employee.fullName,
      }),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translateService.instant('COMMON.DELETE'),
      rejectLabel: this.translateService.instant('COMMON.CANCEL'),
      acceptButtonProps: { severity: 'danger' },
      accept: () => {
        this.employeeService
          .delete(employee.userId)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.employeeService.showDeleteSuccess();
              this.load();
            },
            error: (err) => this.employeeService.showDeleteError(err),
          });
      },
    });
  }

  openAssignStores(employee: EmployeeDto) {
    this.assigningEmployee = employee;
    this.selectedStoreIds = [...(employee.assignedStoreIds ?? [])];
    this.assignDialogVisible = true;
  }

  closeAssignDialog() {
    this.assignDialogVisible = false;
    this.assigningEmployee = null;
    this.selectedStoreIds = [];
  }

  toggleStore(storeId: string) {
    const index = this.selectedStoreIds.indexOf(storeId);

    if (index >= 0) {
      this.selectedStoreIds.splice(index, 1);
      return;
    }

    this.selectedStoreIds.push(storeId);
  }

  isStoreSelected(storeId: string): boolean {
    return this.selectedStoreIds.includes(storeId);
  }

  saveAssignStores() {
    if (!this.assigningEmployee) {
      return;
    }

    this.assigning = true;
    this.employeeService
      .assignStores(this.assigningEmployee.userId, this.selectedStoreIds)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.closeAssignDialog();
          this.load();
        },
        error: () => {
          this.assigning = false;
        },
        complete: () => {
          this.assigning = false;
        },
      });
  }

  openPermDialog(employee: EmployeeDto) {
    this.permEmployee = employee;
    this.selectedPermissions = this.parsePermissionKeys(employee.permissions);
    this.permDialogVisible = true;
  }

  closePermDialog() {
    this.permDialogVisible = false;
    this.permEmployee = null;
    this.selectedPermissions = [];
  }

  togglePerm(key: PermissionKey) {
    if (this.isPermSelected(key)) {
      this.selectedPermissions = this.selectedPermissions.filter(
        (permission) => permission !== key,
      );
      return;
    }

    this.selectedPermissions = [...this.selectedPermissions, key];
  }

  isPermSelected(key: PermissionKey): boolean {
    return this.selectedPermissions.includes(key);
  }

  applyTemplate(template: RoleTemplate) {
    this.selectedPermissions = [...template.permissions];
  }

  isGroupFullySelected(group: PermissionGroup): boolean {
    return group.permissions.every((permission) =>
      this.selectedPermissions.includes(permission.key),
    );
  }

  toggleGroupAll(group: PermissionGroup) {
    if (this.isGroupFullySelected(group)) {
      this.selectedPermissions = this.selectedPermissions.filter(
        (selectedKey) => !group.permissions.some((permission) => permission.key === selectedKey),
      );
      return;
    }

    const nextPermissions = new Set(this.selectedPermissions);
    group.permissions.forEach((permission) => nextPermissions.add(permission.key));
    this.selectedPermissions = Array.from(nextPermissions);
  }

  savePermissions() {
    if (!this.permEmployee) {
      this.closePermDialog();
      return;
    }

    this.savingPerms = true;
    this.employeeService
      .assignPermissions(this.permEmployee.userId, this.selectedPermissions)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.closePermDialog();
          this.load();
        },
        error: () => {
          this.savingPerms = false;
        },
        complete: () => {
          this.savingPerms = false;
        },
      });
  }

  getInitials(fullName: string): string {
    return (fullName ?? '')
      .trim()
      .split(/\s+/)
      .map((word) => word[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }

  getPermissionSummary(rawPermissions: string | null): string {
    this.currentLang();

    const permissions = this.parsePermissionKeys(rawPermissions);
    if (!permissions.length) {
      return 'Ruxsatlar belgilanmagan';
    }

    return permissions
      .map((permission) => this.permissionLabelMap().get(permission) ?? permission)
      .join(', ');
  }

  private saveCreate() {
    if (
      !this.empForm.fullName.trim() ||
      !this.empForm.phoneNumber.trim() ||
      !this.empForm.password.trim()
    ) {
      return;
    }

    this.saving = true;
    this.employeeService
      .create({
        fullName: this.empForm.fullName,
        phoneNumber: this.empPhoneCode + this.empForm.phoneNumber,
        password: this.empForm.password,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.closeForm();

          if (response.data?.plainPin) {
            this.createdPin = response.data.plainPin;
            this.createdName = response.data.fullName;
          }

          this.load();
        },
        error: () => {
          this.saving = false;
        },
        complete: () => {
          this.saving = false;
        },
      });
  }

  private saveEdit() {
    if (!this.editingEmployee || !this.empForm.fullName.trim()) {
      return;
    }

    this.saving = true;
    const password = this.empForm.password.trim();
    this.employeeService
      .update(this.editingEmployee.userId, {
        fullName: this.empForm.fullName,
        isActive: this.empForm.isActive,
        phoneNumber: this.empForm.phoneNumber.trim() || undefined,
        password: password || undefined,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.closeForm();
          this.load();
        },
        error: () => {
          this.saving = false;
        },
        complete: () => {
          this.saving = false;
        },
      });
  }

  openScheduleDialog(employee: EmployeeDto) {
    this.scheduleEmployee = employee;
    this.scheduleForm = this.defaultScheduleForm();
    this.scheduleDialogVisible = true;
    this.scheduleLoading.set(true);
    this.employeeService
      .getWorkSchedule(employee.userId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (res.data) this.applySchedule(res.data);
          this.scheduleLoading.set(false);
        },
        error: () => {
          this.scheduleLoading.set(false);
        },
      });
  }

  closeScheduleDialog() {
    this.scheduleDialogVisible = false;
    this.scheduleEmployee = null;
  }

  toggleWorkDay(bit: number) {
    this.scheduleForm.workDays ^= bit;
  }

  isWorkDay(bit: number): boolean {
    return (this.scheduleForm.workDays & bit) !== 0;
  }

  saveSchedule() {
    if (!this.scheduleEmployee) return;
    this.scheduleSaving.set(true);
    this.employeeService
      .saveWorkSchedule(this.scheduleEmployee.userId, {
        workDays: this.scheduleForm.workDays,
        checkInTime: this.timeToStr(this.scheduleForm.checkInTime),
        checkOutTime: this.timeToStr(this.scheduleForm.checkOutTime),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.scheduleSaving.set(false);
          this.closeScheduleDialog();
        },
        error: () => {
          this.scheduleSaving.set(false);
        },
      });
  }

  private defaultScheduleForm() {
    const checkIn = new Date();
    checkIn.setHours(9, 0, 0, 0);
    const checkOut = new Date();
    checkOut.setHours(18, 0, 0, 0);
    return { workDays: 62, checkInTime: checkIn, checkOutTime: checkOut };
  }

  private applySchedule(dto: WorkScheduleDto) {
    const [inH, inM] = dto.checkInTime.split(':').map(Number);
    const [outH, outM] = dto.checkOutTime.split(':').map(Number);
    const ci = new Date();
    ci.setHours(inH, inM, 0, 0);
    const co = new Date();
    co.setHours(outH, outM, 0, 0);
    this.scheduleForm = { workDays: dto.workDays, checkInTime: ci, checkOutTime: co };
  }

  private timeToStr(d: Date): string {
    return d.toTimeString().slice(0, 5);
  }

  private createEmptyForm(): EmployeeFormModel {
    return {
      fullName: '',
      phoneNumber: '',
      password: '',
      isActive: true,
    };
  }

  private parsePermissionKeys(rawPermissions: string | null): PermissionKey[] {
    if (!rawPermissions) {
      return [];
    }

    return rawPermissions
      .split(',')
      .map((permission) => permission.trim())
      .filter((permission): permission is PermissionKey =>
        CUSTOMER_PERMISSIONS.some((item) => item.key === permission),
      );
  }

  private hasPermissions(employee: EmployeeDto, requiredPermissions: PermissionKey[]): boolean {
    const assignedPermissions = this.parsePermissionKeys(employee.permissions);
    return requiredPermissions.every((permission) => assignedPermissions.includes(permission));
  }

  private isStoreManager(employee: EmployeeDto): boolean {
    const template = ROLE_TEMPLATES.find((item) => item.key === 'storeManager');
    return !!template && this.hasPermissions(employee, template.permissions);
  }

  private isSalesAssociate(employee: EmployeeDto): boolean {
    const managerTemplate = ROLE_TEMPLATES.find((item) => item.key === 'storeManager');
    const associateTemplate = ROLE_TEMPLATES.find((item) => item.key === 'salesAssociate');

    return (
      !!associateTemplate &&
      this.hasPermissions(employee, associateTemplate.permissions) &&
      (!managerTemplate || !this.hasPermissions(employee, managerTemplate.permissions))
    );
  }
}
